import React from 'react';
import formatNumber from 'common/js_utils/formatNumber';
import { AsyncValueDisplay } from 'common/providers/AsyncValues';
import { RowCountType } from './types';
import { useSafeRowCount } from './RowCountUtils';

import { Optional } from 'common/types/nullable';
import getScopedTranslateMethod from 'common/js_utils/getScopedTranslateMethod';

const translate = getScopedTranslateMethod('common.providers.row_count');

/** Row count display properties. */
interface RowCountDisplayProps {
  countType?: RowCountType;
  label?: string;
  padding?: string;
  className?: string;
}

/**
 * Component that displays row count data with loading and error states.
 *
 * @param countType - Type of row count to display (total or filtered). Defaults to Total.
 * @param label - Optional label text or translation key. Supports interpolation with FORMAT_FRAGMENT (%{rowCount}).
 * @param padding - Optional CSS padding value to wrap the component content.
 * @param className - Optional CSS class name to apply to the rendered value span.
 * @returns React component that renders the row count with appropriate loading/error states.
 */
const RowCountDisplay: React.FC<RowCountDisplayProps> = ({
  countType = RowCountType.Total,
  label,
  padding,
  className
}) => {
  // Formatting string fragment:
  const FORMAT_FRAGMENT = '%{rowCount}';

  // Use the hook at the top level of the component
  const safeResult = useSafeRowCount(countType);
  const formatLabel = (cntLabel: Optional<string>, value: unknown): string => {
    if (typeof value !== 'number') return String(value || '');

    const f = formatNumber(value);
    const lbl = cntLabel?.trim() || '';

    // If label is a translation key, fetch it with interpolation
    let result: string;
    if (lbl === 'all_data' || lbl === 'filtered_data' || lbl === 'modified_data' || lbl === 'simple') {
      result = translate(`labels.${lbl}`, { rowCount: f });
    } else {
      result =
        (lbl && (lbl.includes(FORMAT_FRAGMENT) ? lbl.replace(FORMAT_FRAGMENT, f) : `${lbl}: ${f}`)) || f;
    }
    return result;
  };

  const valueDisplay = (
    <AsyncValueDisplay
      result={safeResult}
      renderValue={(value: unknown) => <span className={className}>{formatLabel(label, value)}</span>}
      loadingAriaLabel={translate('progress')}
    />
  );

  return padding ? <div style={{ padding }}>{valueDisplay}</div> : valueDisplay;
};

export default RowCountDisplay;
